import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead, Btn } from "../components/Bits";
import { IMAGES } from "../lib/images";

const CATEGORIES = [
  "All",
  "MMA",
  "BJJ",
  "Wrestling",
  "Judo",
  "Multi-Discipline",
  "Wall Padding",
  "Custom Branding",
];

export default function Installations() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");

  useSeo({
    title: "Combat Gym Installations | MAKO Mats Projects",
    description:
      "Mat and wall-padding installations for MMA, BJJ, wrestling and judo facilities. Project gallery and case studies from MAKO Mats.",
  });

  useEffect(() => {
    http.get("/content/installations").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = category === "All" ? items : items.filter((i) => i.sport === category);

  return (
    <div data-testid="installations-page">
      <PageHero
        eyebrow="Installations"
        title="Built to transform"
        subtitle="training spaces"
        copy="Project examples across disciplines and facility types, from single training zones to complete combat-room installations."
        image={IMAGES.wrestling}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-wrap gap-2.5" data-testid="installation-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`installation-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`border px-4 py-2.5 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  category === c
                    ? "border-[#0F4C81] bg-[#0F4C81] text-white"
                    : "border-[#111111]/15 text-[#3B3B3B] hover:border-[#0F4C81] hover:text-[#0F4C81]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="installation-grid">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.07}>
                <article className="group border border-[#111111]/10 bg-white transition-colors duration-500 hover:border-[#0F4C81]/40">
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#F5F5F2]">
                    <img
                      src={p.image}
                      alt={`${p.sport} installation`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                    />
                    <span className="mako-label absolute left-0 top-0 bg-white/95 px-4 py-2.5 text-[#0F4C81]">
                      {p.sport}
                    </span>
                  </div>
                  <div className="p-7">
                    <p className="font-display text-lg font-bold uppercase tracking-tight text-[#111111]">
                      {p.facility}
                    </p>
                    <p className="mako-label mt-3 text-[#7A7F85]">{p.location}</p>
                    <p className="mt-5 text-sm leading-relaxed text-[#3B3B3B]">{p.summary}</p>
                    <dl className="mt-7 space-y-3 border-t border-[#111111]/10 pt-5 text-xs">
                      <div className="flex justify-between gap-4">
                        <dt className="mako-label text-[#7A7F85]">Products</dt>
                        <dd className="text-right text-[#3B3B3B]">{(p.products || []).join(", ")}</dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="mako-label text-[#7A7F85]">Area</dt>
                        <dd className="text-right text-[#3B3B3B]">{p.area}</dd>
                      </div>
                    </dl>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="mt-14 text-sm text-[#7A7F85]" data-testid="installations-empty">
              No projects in this category yet.
            </p>
          )}
        </div>
      </section>

      <section className="border-t border-[#111111]/10 bg-[#F5F5F2] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead
            eyebrow="Your project next"
            title="Let us plan your installation"
            copy="Send through the room dimensions, disciplines and a few photographs. We will come back with a surface plan."
          />
          <div className="mt-10">
            <Btn to="/contact" variant="dark" data-testid="installations-cta">
              Start your project
            </Btn>
          </div>
        </div>
      </section>
    </div>
  );
}
